import {
  Autorenew,
  ContentCopy,
  DeleteForever,
  Grading,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LockOpen,
  MailOutline,
  MoreVert,
  PostAdd,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
  Stack
} from "@mui/material";
import React from "react";
import { describeAccessCodeStatus } from "../../../utils/accessCodeShare";
import { IAssignment, IManagedUser } from "../../types";

export interface ManagedUserRowDelegate {
  didClickAddInstrument: (userUid: string) => void;
  didClickSeeResults: (assignmentId: number) => void;
  didClickDeleteAssignment: (assignmentId: number, userUid: string) => void;
  didClickCopyAccessShare: (assignmentId: number) => void;
  didClickSendAccessEmail: (assignmentId: number) => void;
  didClickRegenerateAccessCode: (assignmentId: number) => void;
  didClickRevealAccessCode: (assignmentId: number) => void;
}

interface State {
  open: boolean;
  anchorUser: HTMLElement | null;
  anchorAssign: HTMLElement | null;
  selectedAssignId: number | null;
}

interface Props {
  user: IManagedUser;
  delegate: ManagedUserRowDelegate;
  /**
   * Codes already revealed, keyed by assignmentId. A `null` value means the
   * reveal answered "no code"; a missing key means it was never asked, which is
   * what keeps the chip masked until someone clicks it.
   */
  revealedCodes?: Record<number, string | null>;
}

class InvitedUsersRow extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      open: false,
      anchorUser: null,
      anchorAssign: null,
      selectedAssignId: null,
    };
  }

  toggleOpen = () => {
    this.setState((s) => ({ open: !s.open }));
  };

  /**
   * The whole row toggles the panel: every cell in it is read-only text, so a
   * click there cannot mean anything else. What must NOT toggle stops the event
   * itself — the actions button and the menu it opens.
   */
  handleRowClick = () => {
    // A click that ends a text selection was someone copying the e-mail, not
    // asking to expand. An earlier selection is already gone by then (the
    // mousedown clears it), so a non-empty one here belongs to this click.
    if (window.getSelection()?.toString()) {
      return;
    }
    this.toggleOpen();
  };

  /** The row already toggles; letting this bubble would toggle twice. */
  handleChevronClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    this.toggleOpen();
  };

  openUserMenu = (e: React.MouseEvent<HTMLElement>) => {
    // Opening the menu is not "expand this row".
    e.stopPropagation();
    this.setState({ anchorUser: e.currentTarget });
  };
  closeUserMenu = () => {
    this.setState({ anchorUser: null });
  };
  handleAddInstrument = () => {
    this.props.delegate.didClickAddInstrument(this.props.user.uid);
    this.closeUserMenu();
  };

  openAssignMenu = (e: React.MouseEvent<HTMLElement>, assignmentId: number) => {
    this.setState({
      anchorAssign: e.currentTarget,
      selectedAssignId: assignmentId,
    });
  };
  closeAssignMenu = () => {
    this.setState({ anchorAssign: null, selectedAssignId: null });
  };
  handleSeeResults = () => {
    this.props.delegate.didClickSeeResults(this.state.selectedAssignId!);
    this.closeAssignMenu();
  };
  handleDeleteAssignment = () => {
    this.props.delegate.didClickDeleteAssignment(
      this.state.selectedAssignId!,
      this.props.user.uid
    );
    this.closeAssignMenu();
  };
  handleCopyAccessShare = () => {
    this.props.delegate.didClickCopyAccessShare(this.state.selectedAssignId!);
    this.closeAssignMenu();
  };
  handleSendAccessEmail = () => {
    this.props.delegate.didClickSendAccessEmail(this.state.selectedAssignId!);
    this.closeAssignMenu();
  };
  handleRegenerateAccessCode = () => {
    this.props.delegate.didClickRegenerateAccessCode(
      this.state.selectedAssignId!
    );
    this.closeAssignMenu();
  };

  /**
   * Whether this person finished registration. `isInvitation: false` means the
   * identity resolved through the auth `users` table, which is exactly the
   * platform's definition of "registered"; `true` means it resolved through the
   * pending invitation. `undefined` identity means neither resolved (anonymous,
   * or auth unreachable) — not a claim that they have an account, so it reads as
   * unknown and no credential action gets withheld on that basis.
   */
  private hasAccount(): boolean {
    return this.props.user.identity?.isInvitation === false;
  }

  /**
   * The code in force, shown beside "Gerar novo código" so the psychologist can
   * see what they are about to replace.
   *
   * Masked until clicked, and the click must `stopPropagation` — the chip lives
   * inside a MenuItem whose own click regenerates, and swallowing that is the
   * difference between reading a code and silently invalidating it.
   */
  private renderCurrentCodeChip(assignmentId: number) {
    const revealed = this.props.revealedCodes?.[assignmentId];

    if (revealed !== undefined) {
      return (
        <Chip
          size="small"
          variant="outlined"
          label={revealed ?? "sem código"}
          sx={{
            ml: "auto",
            fontFamily: revealed ? "monospace" : undefined,
            letterSpacing: revealed ? "0.08em" : undefined,
          }}
        />
      );
    }

    return (
      <Chip
        size="small"
        variant="outlined"
        label="•••••• ver"
        onClick={(event) => {
          event.stopPropagation();
          this.props.delegate.didClickRevealAccessCode(assignmentId);
        }}
        sx={{ ml: "auto", cursor: "pointer" }}
      />
    );
  }

  /**
   * The credential chip is rendered only when the backend actually reported a
   * status. An older backend omits the field, and a blank cell is honest there —
   * "Sem código" would be a claim this row cannot support.
   *
   * For someone who already registered, the row's status is reported but
   * meaningless: a code row can outlive a registration, so it may still say
   * `active` while the auth mint gate refuses that uid a session. Showing
   * "Código ativo" there would be a straight lie about how they get in.
   */
  renderAccessCodeChip = (assignment: IAssignment) => {
    if (this.hasAccount()) {
      // Names the means, like every other value in this column ("Código
      // ativo", "Código expirado"), so the cell always answers the same
      // question: how does this person get in?
      return (
        <Chip
          label="E-mail e senha"
          color="info"
          variant="outlined"
          size="small"
        />
      );
    }
    if (!assignment.accessCodeStatus) {
      return null;
    }
    const { label, color } = describeAccessCodeStatus(
      assignment.accessCodeStatus
    );
    return <Chip label={label} color={color} variant="outlined" size="small" />;
  };

  renderAssignmentStatusChip = (assignment: IAssignment) => {
    if (assignment.finishedAt) {
      return (
        <Chip
          label="Finalizado"
          color="success"
          size="small"
        />
      );
    }
    if (assignment.startedAt) {
      return (
        <Chip
          label="Iniciado"
          color="info"
          size="small"
        />
      );
    }
    return (
      <Chip
        label="Não iniciado"
        color="default"
        size="small"
      />
    );
  };

  /**
   * The panel is reached from a row that scrolls out of view once the list is
   * long, so it names the respondent it was opened from instead of relying on
   * the reader remembering which chevron they clicked.
   */
  renderAssignmentsPanel = (name: string, email: string | null) => {
    const { user } = this.props;
    const count = user.assignments.length;

    return (
      <Box
        sx={{
          // Indented and accented on the left so it reads as a child of the row
          // above — not as a second, independent table. The chrome mirrors the
          // outer TableContainer (white, 1px grey border, 8px radius) to keep
          // the nesting legible without inventing a new visual language.
          ml: 3,
          mr: 2,
          mt: 1.5,
          mb: 2,
          backgroundColor: "grey.50",
          border: 1,
          borderColor: "grey.300",
          borderLeft: 3,
          borderLeftColor: "primary.main",
          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ px: 2, py: 1.25 }}
        >
          <Typography variant="subtitle2">Aplicações de {name}</Typography>
          <Chip
            size="small"
            label={count === 1 ? "1 aplicação" : `${count} aplicações`}
          />
        </Stack>

        {count === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, pb: 2 }}
          >
            Nenhuma aplicação atribuída. Use "Atribuir Aplicação" no menu de
            ações deste usuário.
          </Typography>
        ) : (
          <Table
            size="small"
            aria-label={`aplicações de ${name}`}
            sx={{
              // A quieter header than the outer table's: same columns grammar,
              // one level down in emphasis, which is what marks the hierarchy.
              "& .MuiTableHead-root": { backgroundColor: "transparent" },
              "& .MuiTableHead-root .MuiTableCell-root": {
                py: 1,
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "text.secondary",
                borderBottom: 1,
                borderColor: "divider",
              },
              "& .MuiTableBody-root .MuiTableCell-root": {
                borderBottom: 1,
                borderColor: "grey.300",
              },
              "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root":
                { borderBottom: 0 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Aplicação</TableCell>
                <TableCell>Data Limite</TableCell>
                <TableCell>Instrumento</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Acesso</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {user.assignments.map(this.renderAssignmentRow)}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  };

  renderAssignmentRow = (assignment: IAssignment) => (
    <TableRow hover key={assignment.assignmentId}>
      <TableCell>{assignment.applicationName}</TableCell>
      <TableCell>
        {assignment.dueDate.toLocaleDateString()}{" "}
        {assignment.dueDate.toLocaleTimeString()}
      </TableCell>
      <TableCell>{assignment.title}</TableCell>
      <TableCell align="center">
        {this.renderAssignmentStatusChip(assignment)}
      </TableCell>
      <TableCell align="center">
        {this.renderAccessCodeChip(assignment)}
      </TableCell>
      <TableCell align="right">
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
        >
          {this.renderSeeResultsButton(assignment)}
          <Tooltip title="Ações da aplicação">
            <IconButton
              aria-label="ações da aplicação"
              size="small"
              onClick={(e) => this.openAssignMenu(e, assignment.assignmentId)}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {this.renderAssignmentMenu(assignment)}
      </TableCell>
    </TableRow>
  );

  /**
   * "Ver Resultados" also stands on its own beside the menu button. It is the
   * one thing this panel is opened for once an application is finished, and it
   * stays in the menu too so the list of actions remains complete in one place.
   *
   * Unfinished keeps the button visible but disabled, for the same reason the
   * menu item is: the column then holds the same controls in every row, and the
   * tooltip says why it cannot be used yet instead of leaving a gap.
   */
  renderSeeResultsButton = (assignment: IAssignment) => {
    if (!assignment.finishedAt) {
      return (
        <Tooltip title="Resultado disponível após finalização">
          {/* A disabled button fires no events — the tooltip needs a live wrapper. */}
          <span>
            <IconButton aria-label="ver resultados" size="small" disabled>
              <Grading fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Ver resultados">
        <IconButton
          aria-label="ver resultados"
          size="small"
          onClick={() =>
            this.props.delegate.didClickSeeResults(assignment.assignmentId)
          }
        >
          <Grading fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  renderAssignmentMenu = (assignment: IAssignment) => {
    const { anchorAssign, selectedAssignId } = this.state;
    const finished = Boolean(assignment.finishedAt);

    return (
      <Menu
        anchorEl={anchorAssign}
        open={
          Boolean(anchorAssign) && selectedAssignId === assignment.assignmentId
        }
        onClose={this.closeAssignMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {finished ? (
          <MenuItem onClick={this.handleSeeResults}>
            <Grading fontSize="small" sx={{ mr: 1 }} />
            Ver Resultados
          </MenuItem>
        ) : (
          <Tooltip
            title="Resultado disponível após finalização"
            placement="left"
          >
            <div>
              <MenuItem disabled>
                <Grading fontSize="small" sx={{ mr: 1 }} />
                Ver Resultados
              </MenuItem>
            </div>
          </Tooltip>
        )}

        <Divider />

        {/* Enabled even after finishing: codes stay valid until the due date so
            the respondent can still open their own devolutiva. */}
        <MenuItem onClick={this.handleCopyAccessShare}>
          <ContentCopy fontSize="small" sx={{ mr: 1 }} />
          Copiar texto de acesso
        </MenuItem>

        {/* The label follows what actually goes out. For a registered person the
            backend sends the `loginShare` template — instrument name plus the
            platform link, no credential — so calling it "enviar acesso" would
            promise something the e-mail does not carry. */}
        <MenuItem onClick={this.handleSendAccessEmail}>
          <MailOutline fontSize="small" sx={{ mr: 1 }} />
          {this.hasAccount()
            ? "Enviar notificação por e-mail"
            : "Enviar acesso por e-mail"}
        </MenuItem>

        {/* Withheld once the person has an account: the whole credential exists
            to serve someone who has NOT registered, and a code minted for a
            registered uid is refused at login — so the action could only
            produce a code that never works. */}
        {this.hasAccount() ? (
          <Tooltip
            title="Esta pessoa já criou a conta — ela entra com e-mail e senha, sem código."
            placement="left"
          >
            <div>
              <MenuItem disabled>
                <Autorenew fontSize="small" sx={{ mr: 1 }} />
                Gerar novo código
              </MenuItem>
            </div>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              assignment.accessCodeStatus === "locked"
                ? "Libera o bloqueio de todas as aplicações desta pessoa e gera um código novo — o antigo para de funcionar, então reenvie o acesso."
                : "Gera um código novo e libera bloqueio por tentativas. Quem já entrou continua até a sessão expirar."
            }
            placement="left"
          >
            <MenuItem
              onClick={this.handleRegenerateAccessCode}
              sx={{ gap: 1 }}
            >
              {assignment.accessCodeStatus === "locked" ? (
                <LockOpen fontSize="small" sx={{ mr: 1 }} />
              ) : (
                <Autorenew fontSize="small" sx={{ mr: 1 }} />
              )}
              {/* Locked is what the psychologist came to fix, so the label leads
                  with it — but it still names the new code, because the one
                  already shared stops working and has to be sent again. */}
              {assignment.accessCodeStatus === "locked"
                ? "Destravar e gerar novo código"
                : "Gerar novo código"}
              {this.renderCurrentCodeChip(assignment.assignmentId)}
            </MenuItem>
          </Tooltip>
        )}

        <Divider />

        {!finished ? (
          <MenuItem onClick={this.handleDeleteAssignment}>
            <DeleteForever fontSize="small" sx={{ mr: 1 }} />
            Remover Aplicação
          </MenuItem>
        ) : (
          <Tooltip
            title="Não é possível remover após finalização"
            placement="left"
          >
            <div>
              <MenuItem disabled>
                <DeleteForever fontSize="small" sx={{ mr: 1 }} />
                Remover Aplicação
              </MenuItem>
            </div>
          </Tooltip>
        )}
      </Menu>
    );
  };

  render() {
    const { user } = this.props;
    const { open, anchorUser } = this.state;
    const identity = user.identity;
    const name = identity
      ? `${identity.firstName} ${identity.lastName}`
      : "Anônimo";
    const email = identity?.email ?? "-";
    const invited = identity?.isInvitation ?? false;
    const instrumentCount = user.assignments.length;
    const userMenuOpen = Boolean(anchorUser);
    const panelId = `assignments-${user.uid}`;

    return (
      <>
        <TableRow
          hover
          onClick={this.handleRowClick}
          sx={{
            cursor: "pointer",
            "& > *": { borderBottom: "unset" },
            // Tinting the open row and the panel's row below it turns the pair
            // into one band, so the expanded block is unmistakably this
            // person's even when several users are open at once.
            ...(open && { backgroundColor: "grey.100" }),
          }}
        >
          <TableCell>{name}</TableCell>
          <TableCell>{email}</TableCell>
          <TableCell align="center">{instrumentCount}</TableCell>
          <TableCell align="center">
            {invited ? <Chip label="Em aberto" color="warning" variant="outlined" size="small" /> : <Chip label="Aceito" color="success" variant="outlined" size="small" />}
          </TableCell>
          <TableCell align="right">
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent="flex-end"
            >
              {/* Left of the actions button and deliberately quieter than it:
                  expanding only reveals what the row already counts, while the
                  filled button is where things actually happen. */}
              <Tooltip title={open ? "Ocultar aplicações" : "Ver aplicações"}>
                <IconButton
                  aria-label="expand row"
                  aria-expanded={open}
                  aria-controls={panelId}
                  size="small"
                  onClick={this.handleChevronClick}
                  sx={{ color: open ? "primary.main" : "text.secondary" }}
                >
                  {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              </Tooltip>

              <IconButton
                aria-label="actions"
                size="medium"
                sx={{
                  backgroundColor: "grey.200",
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "grey.300",
                  },
                }}
                onClick={this.openUserMenu}
              >
                <MoreVert />
              </IconButton>
            </Stack>
            <Menu
              anchorEl={anchorUser}
              open={userMenuOpen}
              onClose={this.closeUserMenu}
              // The menu renders in a portal, but React bubbles its events
              // through the component tree — so its items and its backdrop
              // would otherwise land on the row's click handler.
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={this.handleAddInstrument}>
                <PostAdd fontSize="small" sx={{ mr: 1 }} />
                Atribuir Aplicação
              </MenuItem>
            </Menu>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell
            colSpan={5}
            id={panelId}
            sx={{ p: 0, ...(open && { backgroundColor: "grey.100" }) }}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              {this.renderAssignmentsPanel(name, identity ? email : null)}
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }
}

export default InvitedUsersRow;
